import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/trpc/trpc";

export const Route = createFileRoute("/_authenticated/admin/health")({
  component: AdminSalonHealthPage,
  // The search lives in the URL so it survives a trip to a salon and back.
  validateSearch: z.object({ q: z.string().optional() }),
});

const alertLabels = {
  no_panel_activity: "Nie loguje się do panelu",
  no_client_activity: "Klienci nie otwierają aplikacji",
  no_orders: "Brak zamówień",
  no_first_order: "Bez pierwszego zamówienia",
} as const;

const DAY_MS = 24 * 60 * 60 * 1000;

function daysSince(iso: string) {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS));
}

/** "3 dni temu" for a timestamp; a dash when there is nothing to count from. */
function ago(iso: string | null) {
  if (!iso) return "—";
  const days = daysSince(iso);
  if (days === 0) return "dziś";
  if (days === 1) return "wczoraj";
  return `${days} dni temu`;
}

function AdminSalonHealthPage() {
  const { data, isLoading } = trpc.salonHealth.overview.useQuery();
  const navigate = useNavigate({ from: Route.fullPath });
  const utils = trpc.useUtils();
  const runNow = trpc.salonHealth.runNow.useMutation({
    onSuccess: (r) => {
      utils.salonHealth.overview.invalidate();
      toast.success(
        `Zmierzono salony: ${r.salons}. Nowe alerty: ${r.opened}, ustąpiło: ${r.resolved}, powiadomienia w kolejce: ${r.announced}.`,
      );
    },
    onError: (err) => toast.error(err.message),
  });
  // Typed text is held locally and mirrored to the URL: driving the input
  // straight from the URL can drop keystrokes while a navigation is pending.
  const [q, setQ] = useState(Route.useSearch().q ?? "");

  const needle = q.trim().toLocaleLowerCase("pl");
  const all = data ?? [];
  // The ones that need a call first.
  const salons = all
    .filter(
      (s) =>
        !needle ||
        s.name.toLocaleLowerCase("pl").includes(needle) ||
        s.emails.some((email) => email.toLowerCase().includes(needle)),
    )
    .sort(
      (a, b) =>
        b.alerts.length - a.alerts.length ||
        a.name.localeCompare(b.name, "pl"),
    );
  const needAttention = all.filter((s) => s.alerts.length > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Statystyki salonów</h1>
          <p className="text-sm text-muted-foreground">
            Aktywne salony: {all.length}, wymagające uwagi: {needAttention}.
            Dane odświeżają się raz dziennie o 6:00.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={runNow.isPending}
          onClick={() => runNow.mutate()}
          title="Mierzy stan na tę chwilę i wysyła nowe alerty"
        >
          <RefreshCw
            className={`mr-1 size-4 ${runNow.isPending ? "animate-spin" : ""}`}
          />
          Odśwież teraz
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Szukaj po nazwie lub emailu..."
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            navigate({
              search: { q: e.target.value || undefined },
              replace: true,
            });
          }}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Ładowanie...</p>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Salon</th>
                  <th className="px-4 py-3 font-medium">Alerty</th>
                  <th className="px-4 py-3 font-medium">Panel</th>
                  <th className="px-4 py-3 font-medium">Aktywni klienci (30 dni)</th>
                  <th className="px-4 py-3 font-medium">Ostatnie zamówienie</th>
                  <th className="px-4 py-3 font-medium">Zamówienia (7 dni)</th>
                  <th className="px-4 py-3 font-medium">Zmiana oferty</th>
                  <th className="px-4 py-3 font-medium">Promocja</th>
                </tr>
              </thead>
              <tbody>
                {salons.map((salon) => {
                  const s = salon.snapshot;
                  const openSalon = () =>
                    navigate({
                      to: "/admin/salons/$id",
                      params: { id: salon.salonId },
                      search: { from: "health" },
                    });
                  return (
                    <tr
                      key={salon.salonId}
                      onClick={openSalon}
                      className={`cursor-pointer hover:bg-muted/50 ${
                        salon.alerts.length > 0
                          ? "border-b border-l-4 border-l-red-500 bg-red-50/60 last:border-b-0 dark:bg-red-950/20"
                          : "border-b last:border-0"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">
                        <Link
                          to="/admin/salons/$id"
                          params={{ id: salon.salonId }}
                          search={{ from: "health" }}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                        >
                          {salon.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {salon.alerts.map((alert) => (
                            <Badge
                              key={alert.type}
                              variant="destructive"
                              title={`od ${daysSince(alert.since)} dni`}
                            >
                              {alertLabels[alert.type]}
                            </Badge>
                          ))}
                          {salon.alerts.length === 0 && (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                      {s ? (
                        <>
                          <td className="px-4 py-3 text-muted-foreground">
                            {ago(s.lastPanelActiveAt)}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {s.clientsActive30d ?? "—"} z {s.clientsTotal}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {ago(s.lastOrderAt)}
                          </td>
                          <td
                            className="px-4 py-3 text-muted-foreground"
                            title="Średnia tygodniowa z poprzednich 4 tygodni"
                          >
                            {s.orders7d}{" "}
                            <span className="text-xs">
                              (śr.{" "}
                              {(s.ordersPrev28d / 4).toLocaleString("pl-PL", {
                                maximumFractionDigits: 1,
                              })}
                              )
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {ago(s.lastCatalogChangeAt)}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {ago(s.lastPromotionAt)}
                          </td>
                        </>
                      ) : (
                        <td
                          colSpan={6}
                          className="px-4 py-3 text-muted-foreground"
                        >
                          Pierwszy pomiar pojawi się po najbliższym przebiegu.
                        </td>
                      )}
                    </tr>
                  );
                })}
                {salons.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      {needle
                        ? "Żaden salon nie pasuje do wyszukiwania"
                        : "Brak aktywnych salonów"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
